<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { useWindowSize } from '@vueuse/core';
  import { MpLoadingContainer, openMpConfirmModal } from '@borg/ui';
  import { ExtractType, UserProfile, UserSkill } from '@borg/types';
  import { isArray, isNumber, isObject, isString } from '@borg/utils';
  import { getSalaryString } from '@borg/core/utils/string';

  import {
    useUserDriverLicencesStore,
    useUserLanguagesStore,
    useUserProfileStore,
  } from '@/layers/MyProfile/stores/userProfile.store';
  import { getSkillLevels } from '@/layers/MyProfile/utils/skills';

  interface ModalManager {
    type:
      | 'personalInformation'
      | keyof ExtractType<UserProfile, unknown[]>
      | 'idealJob'
      | undefined;
    visible?: boolean;
  }

  interface ModalManagerEdit<
    T extends
      | {
          id: string | number;
        }
      | unknown,
  > extends ModalManager {
    item: T;
  }

  const defaultModalState: ModalManagerEdit<unknown> = {
    type: undefined,
    item: undefined,
    visible: false,
  } as const;

  const props = defineProps<{
    isPreview?: boolean;
  }>();

  const { t } = useI18n();
  const projectConfig = useProjectConfig();
  const userProfileStore = useUserProfileStore();
  const userLanguagesStore = useUserLanguagesStore();
  const userDriverLicencesStore = useUserDriverLicencesStore();
  const { width: windowWidth } = useWindowSize();

  const skillLevels = getSkillLevels();

  const { data: projects, refresh: refreshProjects } = achievementsRepository.getMany('projects');
  const { data: awards, refresh: refreshAwards } = achievementsRepository.getMany('awards');
  const { data: conferences, refresh: refreshConferences } =
    achievementsRepository.getMany('conferences');
  const { data: bibliography, refresh: refreshBibliography } =
    achievementsRepository.getMany('bibliography');

  const { data: digitalSkills, refresh: refreshDigitalSkills } =
    skillsRepository.getMany('digital');
  const { data: otherSkills, refresh: refreshOtherSkills } = skillsRepository.getMany('other');

  const { data: educations, refresh: refreshEducations } = educationsRepository.getMany(false);
  const { data: trainings, refresh: refreshTrainings } = educationsRepository.getMany(true);

  const { data: workExperience, refresh: refreshWorkExperience } =
    workExperienceRepository.getMany(false);
  const { data: volunteerExperience, refresh: refreshVolunteerExperience } =
    workExperienceRepository.getMany(true);

  const { profile } = storeToRefs(userProfileStore);
  const changeMade = ref(false);
  const modal = ref<ModalManager>({ ...defaultModalState });

  const ratioTriggerWidth = 1168;
  const ratio = computed(() =>
    !props.isPreview || windowWidth.value >= ratioTriggerWidth
      ? 1
      : Math.round((windowWidth.value / ratioTriggerWidth) * 1000) / 1000,
  );

  const modalId = computed(() =>
    'item' in modal.value &&
    isObject(modal.value.item) &&
    'id' in modal.value.item &&
    (isNumber(modal.value.item.id) || isString(modal.value.item.id))
      ? modal.value.item.id
      : undefined,
  );

  const driverLicences = computed(() =>
    stringifyArray(userDriverLicencesStore.items, (v) => `${v.name}`),
  );
  const isIdealJobSpecified = computed(
    () =>
      !!(
        profile.value?.idealJob &&
        (profile.value.idealJob.locations.length ||
          profile.value.idealJob.positions.length ||
          profile.value.idealJob.employmentTypes.length ||
          profile.value.idealJob.salary?.from ||
          profile.value.idealJob.salary?.to)
      ),
  );
  const idealJobLocations = computed(() =>
    stringifyArray(profile.value?.idealJob?.locations, (v) => `${v?.name}`),
  );
  const idealJobPositions = computed(() =>
    stringifyArray(profile.value?.idealJob?.positions, (v) => `${v?.name}`),
  );
  const idealJobEmploymentTypes = computed(() =>
    stringifyArray(profile.value?.idealJob?.employmentTypes, (v) => `${v?.name}`),
  );
  const idealJobSalary = computed(() =>
    getSalaryString(
      profile.value?.idealJob?.salary?.from,
      profile.value?.idealJob?.salary?.to,
      null,
      profile.value?.idealJob?.salary?.currency,
    ),
  );

  function getSkillLevelLabel(skill: UserSkill) {
    return skillLevels.find((i) => i.value === skill.level)?.label;
  }

  function stringifyArray<T>(items: T[] | undefined, toString: (item: T) => string): string {
    return isArray(items) && items.length > 0 ? items.map(toString).join(', ') : '';
  }

  function testDisplay(...items: unknown[][]) {
    if (props.isPreview) return items.some((a) => a.length > 0);
    return true;
  }

  function onAddModalOpen(input: ModalManager) {
    modal.value = { ...defaultModalState, ...input, visible: true };
  }

  function onEditModalOpen<
    T extends {
      id: string | number;
    },
  >(input: ModalManagerEdit<T>) {
    modal.value = { ...defaultModalState, ...input, visible: true };
  }

  function onModalVisibilityUpdate(value: boolean) {
    modal.value = { ...defaultModalState, visible: value };
    if (!value && changeMade.value) {
      userProfileStore.checkPublishable();
      changeMade.value = false;
    }
  }

  function getPositionSubtitle(companyName: string | undefined, location: string | undefined) {
    if (!companyName && !location) {
      return null;
    }
    if (companyName?.trim() && location?.trim()) {
      return `${companyName}, ${location}`;
    }
    if (companyName && !location) {
      return companyName;
    }
    return location;
  }

  async function onItemDelete<
    T extends {
      id: string | number;
    },
  >(input: ModalManagerEdit<T>) {
    const confirmText = () => {
      switch (input.type) {
        case 'workExperience':
          return t('MY_PROFILE_PAGE.DELETE_WORK_EXPERIENCE_CONFIRM_MODAL.TEXT');
        case 'volunteerExperience':
          return t('MY_PROFILE_PAGE.DELETE_VOLUNTEER_EXPERIENCE_CONFIRM_MODAL.TEXT');
        case 'educations':
        case 'trainings':
          return t('MY_PROFILE_PAGE.DELETE_EDUCATION_CONFIRM_MODAL.TEXT');
        case 'digitalSkills':
        case 'otherSkills':
          return t('MY_PROFILE_PAGE.DELETE_SKILL_CONFIRM_MODAL.TEXT');
        case 'languages':
          return t('MY_PROFILE_PAGE.DELETE_LANGUAGE_CONFIRM_MODAL.TEXT');
        case 'projects':
        case 'awards':
        case 'conferences':
        case 'bibliography':
          return t('MY_PROFILE_PAGE.DELETE_ACHIEVEMENT_CONFIRM_MODAL.TEXT');
      }
    };
    const confirmResponse = await openMpConfirmModal({
      text: confirmText(),
      confirmButtonLabel: t('YES'),
      cancelButtonLabel: t('NO'),
    });
    if (confirmResponse && input.item.id) {
      if (input.type === 'workExperience') {
        await workExperienceRepository.delete(input.item.id.toString());
        refreshWorkExperience();
      } else if (input.type === 'volunteerExperience') {
        await workExperienceRepository.delete(input.item.id.toString());
        refreshVolunteerExperience();
      } else if (input.type === 'educations') {
        await educationsRepository.delete(input.item.id.toString());
        refreshEducations();
      } else if (input.type === 'trainings') {
        await educationsRepository.delete(input.item.id.toString());
        refreshTrainings();
      } else if (input.type === 'digitalSkills') {
        await skillsRepository.delete(input.item.id);
        refreshDigitalSkills();
      } else if (input.type === 'otherSkills') {
        await skillsRepository.delete(input.item.id);
        refreshOtherSkills();
      } else if (input.type === 'languages') {
        await userLanguagesStore.remove(input.item.id);
      } else if (input.type === 'projects') {
        await achievementsRepository.delete(input.item.id);
        refreshProjects();
      } else if (input.type === 'awards') {
        await achievementsRepository.delete(input.item.id);
        refreshAwards();
      } else if (input.type === 'conferences') {
        await achievementsRepository.delete(input.item.id);
        refreshConferences();
      } else if (input.type === 'bibliography') {
        await achievementsRepository.delete(input.item.id);
        refreshBibliography();
      }
      userProfileStore.checkPublishable();
    }
  }

  onMounted(async () => {
    if (userProfileStore.initState === 'isInitial') {
      await userProfileStore.lazyInit();
    }
  });
</script>

<template>
  <div
    :data-test="props.isPreview ? 'my-profile-container-preview' : 'my-profile-container'"
    :class="{
      'my-profile-container': true,
      'my-profile-container--preview': props.isPreview,
    }"
  >
    <MyProfileSection :preview="isPreview">
      <template
        v-if="isPreview"
        #title
      >
        <Avatar size="xl" />
      </template>
      <MyProfilePersonalInformation
        :preview="isPreview"
        @edit="onAddModalOpen({ type: 'personalInformation' })"
      />
    </MyProfileSection>
    <MpLoadingContainer :is-loading="userProfileStore.initState === 'inProgress'">
      <MyProfileSection
        v-if="testDisplay(workExperience || [], volunteerExperience || [])"
        :title="$t('MY_PROFILE_PAGE.WORK_EXPERIENCE_SECTION_TITLE')"
        :preview="isPreview"
      >
        <MyProfileSubSection
          v-if="testDisplay(workExperience || [])"
          test="work-experience"
          :preview="isPreview"
          @add="onAddModalOpen({ type: 'workExperience' })"
        >
          <div class="profile-container__entry-list">
            <MyProfileEntry
              v-for="item in workExperience"
              :key="item.id"
              test="work-experience"
              :title="item.position"
              :subtitle="getPositionSubtitle(item.companyName, item.location)"
              :from="toDate(item.startedOn)"
              :to="toDate(item.endedOn)"
              show-duration
              :description="item.description"
              :preview="isPreview"
              @edit="onEditModalOpen({ type: 'workExperience', item })"
              @delete="onItemDelete({ type: 'workExperience', item })"
            />
          </div>
        </MyProfileSubSection>
        <MyProfileSubSection
          v-if="testDisplay(volunteerExperience || [])"
          :title="$t('MY_PROFILE_PAGE.VOLUNTEER_EXPERIENCE_SECTION_TITLE')"
          :preview="isPreview"
          @add="onAddModalOpen({ type: 'volunteerExperience' })"
        >
          <div class="profile-container__entry-list">
            <MyProfileEntry
              v-for="item in volunteerExperience"
              :key="item.id"
              :title="item.position"
              :subtitle="getPositionSubtitle(item.companyName, item.location)"
              :from="toDate(item.startedOn)"
              :to="toDate(item.endedOn)"
              show-duration
              :description="item.description"
              :preview="isPreview"
              @edit="onEditModalOpen({ type: 'volunteerExperience', item })"
              @delete="onItemDelete({ type: 'volunteerExperience', item })"
            />
          </div>
        </MyProfileSubSection>
      </MyProfileSection>
      <MyProfileSection
        v-if="testDisplay(educations || [], trainings || [])"
        :title="$t('MY_PROFILE_PAGE.EDUCATION_SECTION_TITLE')"
        :preview="isPreview"
      >
        <MyProfileSubSection
          v-if="testDisplay(educations || [])"
          :preview="isPreview"
          @add="onAddModalOpen({ type: 'educations' })"
        >
          <div class="profile-container__entry-list">
            <MyProfileEntry
              v-for="item in educations"
              :key="item.id"
              :title="item.institution.name"
              :subtitle="item.orientation"
              :from="toDate(item.startedOn)"
              :to="toDate(item.endedOn)"
              show-duration
              dates-format="year"
              :preview="isPreview"
              @edit="onEditModalOpen({ type: 'educations', item })"
              @delete="onItemDelete({ type: 'educations', item })"
            />
          </div>
        </MyProfileSubSection>
        <MyProfileSubSection
          v-if="testDisplay(trainings || [])"
          :title="$t('MY_PROFILE_PAGE.TRAININGS_SECTION_TITLE')"
          :preview="isPreview"
          @add="onAddModalOpen({ type: 'trainings' })"
        >
          <div class="profile-container__entry-list">
            <MyProfileEntry
              v-for="item in trainings"
              :key="item.id"
              :title="item.institution.name"
              :subtitle="item.orientation"
              :from="toDate(item.startedOn)"
              :to="toDate(item.endedOn)"
              show-duration
              dates-format="year"
              :preview="isPreview"
              @edit="onEditModalOpen({ type: 'trainings', item })"
              @delete="onItemDelete({ type: 'trainings', item })"
            />
          </div>
        </MyProfileSubSection>
      </MyProfileSection>
      <template v-if="projectConfig.features.extendedUserProfile">
        <MyProfileSection
          v-if="
            testDisplay(
              digitalSkills || [],
              otherSkills || [],
              userLanguagesStore.items,
              userDriverLicencesStore.items,
            )
          "
          :title="$t('MY_PROFILE_PAGE.SKILLS_AND_KNOWLEDGE_SECTION_TITLE')"
          :preview="isPreview"
        >
          <MyProfileSubSection
            v-if="testDisplay(digitalSkills || [])"
            :title="$t('MY_PROFILE_PAGE.DIGITAL_SKILLS_SECTION_TITLE')"
            :preview="isPreview"
            test="digital-skills"
            @add="onAddModalOpen({ type: 'digitalSkills' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-for="item in digitalSkills"
                :key="item.id"
                test="digital-skills"
                :title="item.skill.name"
                :subtitle="getSkillLevelLabel(item)"
                :description="item.description"
                :preview="isPreview"
                @edit="onEditModalOpen({ type: 'digitalSkills', item })"
                @delete="onItemDelete({ type: 'digitalSkills', item })"
              />
            </div>
          </MyProfileSubSection>
          <MyProfileSubSection
            v-if="testDisplay(otherSkills || [])"
            :title="$t('MY_PROFILE_PAGE.OTHER_SKILLS_SECTION_TITLE')"
            :preview="isPreview"
            test="other-skills"
            @add="onAddModalOpen({ type: 'otherSkills' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-for="item in otherSkills"
                :key="item.id"
                test="other-skills"
                :title="item.skill.name"
                :subtitle="getSkillLevelLabel(item)"
                :description="item.description"
                :preview="isPreview"
                @edit="onEditModalOpen({ type: 'otherSkills', item })"
                @delete="onItemDelete({ type: 'otherSkills', item })"
              />
            </div>
          </MyProfileSubSection>
          <MyProfileSubSection
            v-if="testDisplay(userLanguagesStore.items)"
            :title="$t('MY_PROFILE_PAGE.LANGUAGES_SECTION_TITLE')"
            :preview="isPreview"
            @add="onAddModalOpen({ type: 'languages' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-for="item in userLanguagesStore.items"
                :key="item.id"
                :title="item.language.name"
                :subtitle="item.languageLevel.name"
                :preview="isPreview"
                @edit="onEditModalOpen({ type: 'languages', item })"
                @delete="onItemDelete({ type: 'languages', item })"
              />
            </div>
          </MyProfileSubSection>
          <MyProfileSubSection
            v-if="testDisplay(userDriverLicencesStore.items)"
            :title="$t('MY_PROFILE_PAGE.DRIVER_LICENCE_SECTION_TITLE')"
            :preview="isPreview"
            @add="onAddModalOpen({ type: 'driverLicences' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-if="userDriverLicencesStore.items.length"
                :subtitle="driverLicences"
                :preview="isPreview"
                :is-deletable="false"
                @edit="onAddModalOpen({ type: 'driverLicences' })"
              />
            </div>
          </MyProfileSubSection>
        </MyProfileSection>
        <MyProfileSection
          v-if="testDisplay(projects || [], awards || [], conferences || [], bibliography || [])"
          :title="$t('MY_PROFILE_PAGE.ACHIEVEMENTS_SECTION_TITLE')"
          :preview="isPreview"
        >
          <MyProfileSubSection
            v-if="testDisplay(projects || [])"
            :title="$t('MY_PROFILE_PAGE.PROJECTS_SECTION_TITLE')"
            :preview="isPreview"
            @add="onAddModalOpen({ type: 'projects' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-for="item in projects"
                :key="item.id"
                :title="item.name"
                :from="toDate(item.startedOn)"
                :to="toDate(item.endedOn)"
                show-duration
                :description="item.description ?? undefined"
                :preview="isPreview"
                @edit="onEditModalOpen({ type: 'projects', item })"
                @delete="onItemDelete({ type: 'projects', item })"
              />
            </div>
          </MyProfileSubSection>
          <MyProfileSubSection
            v-if="testDisplay(awards || [])"
            :title="$t('MY_PROFILE_PAGE.AWARDS_SECTION_TITLE')"
            :preview="isPreview"
            @add="onAddModalOpen({ type: 'awards' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-for="item in awards"
                :key="item.id"
                :title="item.name"
                :from="toDate(item.startedOn)"
                disable-to-time
                :description="item.description ?? undefined"
                dates-format="year"
                :preview="isPreview"
                @edit="onEditModalOpen({ type: 'awards', item })"
                @delete="onItemDelete({ type: 'awards', item })"
              />
            </div>
          </MyProfileSubSection>
          <MyProfileSubSection
            v-if="testDisplay(conferences || [])"
            :title="$t('MY_PROFILE_PAGE.CONFERENCES_SECTION_TITLE')"
            :preview="isPreview"
            @add="onAddModalOpen({ type: 'conferences' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-for="item in conferences"
                :key="item.id"
                :title="item.name"
                :from="toDate(item.startedOn)"
                disable-to-time
                :description="item.description ?? undefined"
                dates-format="year"
                :preview="isPreview"
                @edit="onEditModalOpen({ type: 'conferences', item })"
                @delete="onItemDelete({ type: 'conferences', item })"
              />
            </div>
          </MyProfileSubSection>
          <MyProfileSubSection
            v-if="testDisplay([bibliography || []])"
            :title="$t('MY_PROFILE_PAGE.BIBLIOGRAPHY_SECTION_TITLE')"
            :preview="isPreview"
            @add="onAddModalOpen({ type: 'bibliography' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-for="item in bibliography"
                :key="item.id"
                :title="item.name"
                :from="toDate(item.startedOn)"
                disable-to-time
                :description="item.description ?? undefined"
                dates-format="year"
                :preview="isPreview"
                @edit="onEditModalOpen({ type: 'bibliography', item })"
                @delete="onItemDelete({ type: 'bibliography', item })"
              />
            </div>
          </MyProfileSubSection>
        </MyProfileSection>
        <MyProfileSection
          v-if="!isPreview"
          :title="$t('MY_PROFILE_PAGE.IDEAL_JOB.TITLE')"
          :preview="isPreview"
          :is-editable="isIdealJobSpecified"
          @edit="onAddModalOpen({ type: 'idealJob' })"
        >
          <MyProfileSubSection
            :show-add-button="!isIdealJobSpecified"
            @add="onAddModalOpen({ type: 'idealJob' })"
          >
            <div class="profile-container__entry-list">
              <MyProfileEntry
                v-if="idealJobLocations"
                :title="$t('MY_PROFILE_PAGE.IDEAL_JOB.LOCATIONS_LABEL')"
                :subtitle="idealJobLocations"
                :preview="isPreview"
                :is-editable="false"
                :is-deletable="false"
              />
              <MyProfileEntry
                v-if="idealJobPositions"
                :title="$t('MY_PROFILE_PAGE.IDEAL_JOB.POSITIONS_LABEL')"
                :subtitle="idealJobPositions"
                :preview="isPreview"
                :is-editable="false"
                :is-deletable="false"
              />
              <MyProfileEntry
                v-if="idealJobEmploymentTypes"
                :title="$t('MY_PROFILE_PAGE.IDEAL_JOB.EMPLOYMENT_TYPES_LABEL')"
                :subtitle="idealJobEmploymentTypes"
                :preview="isPreview"
                :is-editable="false"
                :is-deletable="false"
              />
              <MyProfileEntry
                v-if="idealJobSalary"
                :title="$t('MY_PROFILE_PAGE.IDEAL_JOB.SALARY_LABEL')"
                :subtitle="idealJobSalary"
                :preview="isPreview"
                :is-editable="false"
                :is-deletable="false"
              />
            </div>
          </MyProfileSubSection>
        </MyProfileSection>
      </template>
    </MpLoadingContainer>
    <template v-if="!props.isPreview">
      <MyProfileEditModalPersonalInformation
        :is-open="modal.type === 'personalInformation' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="changeMade = true"
      />
      <MyProfileEditModalWorkExperience
        :id="modalId?.toString()"
        :is-open="modal.type === 'workExperience' && !!modal.visible"
        :is-volunteering="false"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshWorkExperience"
      />
      <MyProfileEditModalWorkExperience
        :id="modalId?.toString()"
        :is-open="modal.type === 'volunteerExperience' && !!modal.visible"
        :is-volunteering="true"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshVolunteerExperience"
      />
      <MyProfileEditModalEducation
        :id="modalId?.toString()"
        :is-open="modal.type === 'educations' && !!modal.visible"
        :is-training="false"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshEducations"
      />
      <MyProfileEditModalEducation
        :id="modalId?.toString()"
        :is-open="modal.type === 'trainings' && !!modal.visible"
        :is-training="true"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshTrainings"
      />
      <MyProfileEditModalSkill
        :id="modalId?.toString()"
        type="digital"
        :is-open="modal.type === 'digitalSkills' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshDigitalSkills"
      />
      <MyProfileEditModalSkill
        :id="modalId?.toString()"
        type="other"
        :is-open="modal.type === 'otherSkills' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshOtherSkills"
      />
      <MyProfileEditModalLanguage
        :id="modalId"
        :is-open="modal.type === 'languages' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="changeMade = true"
      />
      <MyProfileEditModalDriverLicences
        :is-open="modal.type === 'driverLicences' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="changeMade = true"
      />
      <MyProfileEditModalAchievement
        :id="modalId"
        type="projects"
        :is-open="modal.type === 'projects' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshProjects"
      />
      <MyProfileEditModalAchievement
        :id="modalId"
        type="awards"
        :is-open="modal.type === 'awards' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshAwards"
      />
      <MyProfileEditModalAchievement
        :id="modalId"
        type="conferences"
        :is-open="modal.type === 'conferences' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshConferences"
      />
      <MyProfileEditModalAchievement
        :id="modalId"
        type="bibliography"
        :is-open="modal.type === 'bibliography' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="refreshBibliography"
      />
      <MyProfileEditModalIdealJob
        :id="modalId"
        :is-open="modal.type === 'idealJob' && !!modal.visible"
        @update:is-open="onModalVisibilityUpdate"
        @change="changeMade = true"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
  .my-profile-container {
    width: 100%;

    &--preview {
      zoom: v-bind(ratio);
    }
  }

  .my-profile-section:not(.my-profile-section--preview) {
    .profile-container__entry-list {
      margin-top: calc(-1 * var(--mp-space-40));
      margin-bottom: calc(-1 * var(--mp-space-40));

      &:empty {
        margin-top: calc(-1 * var(--mp-space-60));
        margin-bottom: calc(-1 * var(--mp-space-60));
      }
    }
  }
</style>
